import { Fragment, useEffect, useState } from "react";
import type { OperationsAttentionKey, OperationsDirectoryDto, OperationsLensKey, OperationsOwnerRowDto, OperationsPenKey } from "@/api/endpoints";
import { InfoTooltip } from "@/components/InfoTooltip";
import { initials } from "@/lib/format";
import { lensLabel, workTypeLabel } from "../operationsLabels";
import { LensTag, UnlensedTag } from "./LensTags";

interface Props {
  owners: OperationsOwnerRowDto[];
  total: OperationsOwnerRowDto;
  periodLabel: string;
  /** Max pending warms past this and heats past three times it (the Blocked clock's threshold). */
  blockedAfterBd: number;
  activeOwner?: string;
  /** The drilled team / division row, highlighted like the drilled owner (2026-10-26). */
  activeTeam?: string;
  activeDivision?: string;
  /** The drilled manager row (2026-10-27). */
  activeManager?: string;
  /** The lens the open-derived columns were cut with; owners with no lensed open item are hidden, the movement columns dim. */
  lens?: OperationsLensKey;
  /** The second lens (who holds the pen, 2026-10-28), cut the same way. */
  pen?: OperationsPenKey;
  /** Work type → weight behind the Weighted open column (settings), plus the weight of any other type. */
  weights: Record<string, number>;
  defaultWeight: number;
  /** The provisioning directory's size and the owners it cannot place (2026-10-26); the footer names them so an alias can be added. */
  directory?: OperationsDirectoryDto;
  /**
   * An owner row drills on click; a team / division row drills from its count cells (the row itself opens or
   * closes the team), with the count's filter preset: Stuck / Blocked as an Attention chip, At risk / Breached
   * as the journey drawer's cohort (Rafael, 2026-10-26).
   */
  onDrill: (target: OwnerDrillTarget, preset?: OwnerDrillPreset) => void;
}

/**
 * What a drill lists: one owner, or a group row by its full path (division, then manager, then team), so the
 * drawer's bucket is exactly the row even when a manager or a team appears under two divisions (an owner sits in
 * their own division, their lead may sit in another; 2026-10-27).
 */
/** An owner row drills to their desk view, or (2026-10-28) to the requests they created / completed in the period. */
export type OwnerDrillTarget = { dOwner: string; dFlow?: "started" | "ended" } | { dDivision: string; dManager?: string; dTeam?: string };
export type OwnerDrillPreset = { attention?: OperationsAttentionKey; startRisk?: "atRisk" | "breached" };

const DIRECTORY_TIP = "An owner is looked up in the provisioning directory three ways, in order: an alias from the Directory:Aliases setting (FBO e-mail = provisioning e-mail), the part of the e-mail before the @, then the full name when exactly one directory user carries it. Owners that match none of these show under No team / No division.";

/** "N owners not in the provisioning directory: A (a@x), B …" or the unavailable notice; null when everyone is placed. */
function DirectoryNote({ directory }: { directory: OperationsDirectoryDto }) {
  if (directory.users === 0) {
    return (
      <span className="ops-directory-note warn">
        Provisioning directory unavailable: every owner shows under No team.
        <InfoTooltip inline text={DIRECTORY_TIP} />
      </span>
    );
  }
  if (directory.unmatchedOwners.length === 0) return null;
  const n = directory.unmatchedOwners.length;
  const list = directory.unmatchedOwners.map((u) => (u.email ? `${u.owner} (${u.email})` : u.owner)).join(", ");
  return (
    <span className="ops-directory-note">
      {n} owner{n === 1 ? "" : "s"} not in the provisioning directory, shown under No team: {list}. Add an alias in Directory:Aliases when provisioning holds them under another e-mail.
      <InfoTooltip inline text={DIRECTORY_TIP} />
    </span>
  );
}

export const NO_DIVISION = "No division";
export const NO_TEAM = "No team";
export const NO_MANAGER = "No manager";
const OPEN_TEAMS_KEY = "ops.ownerTable.openTeams";
/** Manager groups closed by hand (2026-10-27); they open by default so the teams stay one click away. */
const CLOSED_MANAGERS_KEY = "ops.ownerTable.closedManagers";

/** "Onboarding 10 · Extension 5 · …" in Flow row order, for the column tooltip. */
function weightScale(weights: Record<string, number>, defaultWeight: number): string {
  const order = ["Onboarding", "Extension", "Redeployment", "Termination", "ChangeRequest", "DocumentRequest", "Other"];
  const keys = [...order.filter((k) => k in weights), ...Object.keys(weights).filter((k) => !order.includes(k)).sort()];
  return `${keys.map((k) => `${workTypeLabel(k)} ${weights[k]}`).join(" · ")} · other types ${defaultWeight}`;
}

/** "Onboarding 3 × 10 = 30 · Extension 2 × 5 = 10 · 5 journeys": the cell's breakdown. */
export function workloadBreakdown(o: OperationsOwnerRowDto): string {
  if (o.weightedJourneys === 0) return "No open journey carried by this owner's records.";
  const parts = o.workload.map((p) => `${workTypeLabel(p.workType)} ${p.count} × ${p.weight} = ${p.weighted}`);
  return `${parts.join(" · ")} · ${o.weightedJourneys} ${o.weightedJourneys === 1 ? "journey" : "journeys"}`;
}

export interface TeamGroup {
  team: string;
  /** Owners with something open (or a lensed item), in the API's order (max pending first). */
  owners: OperationsOwnerRowDto[];
  /** Owners with nothing open who moved something in the period; folded behind one line. */
  idle: OperationsOwnerRowDto[];
  subtotal: OperationsOwnerRowDto;
}

/** The manager of the teams' leads (2026-10-27): one level between the division and its teams. */
export interface ManagerGroup {
  manager: string;
  teams: TeamGroup[];
  subtotal: OperationsOwnerRowDto;
}

export interface DivisionGroup {
  division: string;
  managers: ManagerGroup[];
  /** Every team of the division, in order, for the counts and the open keys. */
  teams: TeamGroup[];
  subtotal: OperationsOwnerRowDto;
}

/** The same columns summed over a set of owner rows (max for the two age columns, weights merged by work type). */
export function summarize(label: string, rows: OperationsOwnerRowDto[], division: string | null): OperationsOwnerRowDto {
  const sum = (pick: (o: OperationsOwnerRowDto) => number) => rows.reduce((n, o) => n + pick(o), 0);
  const max = (pick: (o: OperationsOwnerRowDto) => number | null) =>
    rows.reduce<number | null>((m, o) => { const v = pick(o); return v == null ? m : m == null ? v : Math.max(m, v); }, null);
  const byType = new Map<string, { count: number; weight: number; weighted: number }>();
  for (const p of rows.flatMap((o) => o.workload)) {
    const cur = byType.get(p.workType) ?? { count: 0, weight: p.weight, weighted: 0 };
    byType.set(p.workType, { count: cur.count + p.count, weight: p.weight, weighted: cur.weighted + p.weighted });
  }
  return {
    owner: label, team: null, department: null, division, manager: null,
    openOwned: sum((o) => o.openOwned), stuck: sum((o) => o.stuck), blocked: sum((o) => o.blocked),
    oldestItemBd: max((o) => o.oldestItemBd), maxPendingBd: max((o) => o.maxPendingBd),
    opening: sum((o) => o.opening), newAssigned: sum((o) => o.newAssigned), departures: sum((o) => o.departures), closing: sum((o) => o.closing),
    identityHolds: rows.every((o) => o.identityHolds),
    weightedOpen: sum((o) => o.weightedOpen), weightedJourneys: sum((o) => o.weightedJourneys),
    atRisk: sum((o) => o.atRisk), breached: sum((o) => o.breached),
    workload: [...byType.entries()].map(([workType, v]) => ({ workType, ...v })),
  };
}

/**
 * Division (the directory's line of business) → manager (the team lead's manager, 2026-10-27) → team (the lead) →
 * owners (Rafael, 2026-10-23). Divisions alphabetical with "No division" last; managers inside a division by their
 * longest-pending item, "No manager" last; teams inside a manager the same way, "No team" last; owners inside a
 * team in the API's order. A team of one is shown as it is.
 */
export function groupOwners(owners: OperationsOwnerRowDto[]): DivisionGroup[] {
  const divisions = new Map<string, Map<string, Map<string, OperationsOwnerRowDto[]>>>();
  for (const o of owners) {
    const division = o.division ?? NO_DIVISION;
    const manager = o.manager ?? NO_MANAGER;
    const team = o.team ?? NO_TEAM;
    const managers = divisions.get(division) ?? new Map<string, Map<string, OperationsOwnerRowDto[]>>();
    const teams = managers.get(manager) ?? new Map<string, OperationsOwnerRowDto[]>();
    teams.set(team, [...(teams.get(team) ?? []), o]);
    managers.set(manager, teams);
    divisions.set(division, managers);
  }
  const last = (key: string, sentinel: string) => (key === sentinel ? 1 : 0);
  const byLoad = (a: { subtotal: OperationsOwnerRowDto }, b: { subtotal: OperationsOwnerRowDto }) =>
    (b.subtotal.maxPendingBd ?? -1) - (a.subtotal.maxPendingBd ?? -1) || b.subtotal.openOwned - a.subtotal.openOwned;
  return [...divisions.entries()]
    .sort(([a], [b]) => last(a, NO_DIVISION) - last(b, NO_DIVISION) || a.localeCompare(b))
    .map(([division, managers]) => {
      const managerGroups: ManagerGroup[] = [...managers.entries()].map(([manager, teams]) => {
        const groups: TeamGroup[] = [...teams.entries()].map(([team, rows]) => ({
          team,
          owners: rows.filter((o) => o.openOwned > 0 || o.weightedJourneys > 0),
          idle: rows.filter((o) => o.openOwned === 0 && o.weightedJourneys === 0),
          subtotal: summarize(team, rows, division),
        }));
        groups.sort((a, b) => last(a.team, NO_TEAM) - last(b.team, NO_TEAM) || byLoad(a, b) || a.team.localeCompare(b.team));
        return { manager, teams: groups, subtotal: summarize(manager, [...teams.values()].flat(), division) };
      });
      managerGroups.sort((a, b) => last(a.manager, NO_MANAGER) - last(b.manager, NO_MANAGER) || byLoad(a, b) || a.manager.localeCompare(b.manager));
      const allTeams = managerGroups.flatMap((m) => m.teams);
      const allRows = [...managers.values()].flatMap((teams) => [...teams.values()].flat());
      return { division, managers: managerGroups, teams: allTeams, subtotal: summarize(`${division} subtotal`, allRows, division) };
    });
}

function readOpenTeams(): Set<string> {
  try {
    const raw = window.localStorage.getItem(OPEN_TEAMS_KEY);
    return new Set(raw ? (JSON.parse(raw) as string[]) : []);
  } catch {
    return new Set();
  }
}

function writeOpenTeams(open: Set<string>): void {
  try {
    window.localStorage.setItem(OPEN_TEAMS_KEY, JSON.stringify([...open]));
  } catch {
    // Browser storage can be unavailable; the state then lives for the page only.
  }
}

function readClosedManagers(): Set<string> {
  try {
    const raw = window.localStorage.getItem(CLOSED_MANAGERS_KEY);
    return new Set(raw ? (JSON.parse(raw) as string[]) : []);
  } catch {
    return new Set();
  }
}

function writeClosedManagers(closed: Set<string>): void {
  try {
    window.localStorage.setItem(CLOSED_MANAGERS_KEY, JSON.stringify([...closed]));
  } catch {
    // Browser storage can be unavailable; the state then lives for the page only.
  }
}

/** Panel 7: owner load (open, weighted, stuck, blocked, oldest, max pending) and period movement, by line of business and team. */
export function OwnerTable({ owners: allOwners, total, periodLabel, blockedAfterBd, activeOwner, activeTeam, activeDivision, activeManager, lens, pen, weights, defaultWeight, directory, onDrill }: Props) {
  const label = lensLabel(lens, pen);
  const owners = label ? allOwners.filter((o) => o.openOwned > 0 || o.weightedJourneys > 0) : allOwners;
  const hidden = allOwners.length - owners.length;
  const groups = groupOwners(owners);
  const teamKeys = groups.flatMap((g) => g.teams.map((t) => `${g.division}/${t.team}`));
  const managerKeys = groups.flatMap((g) => g.managers.map((m) => `${g.division}/${m.manager}`));
  const [open, setOpen] = useState<Set<string>>(readOpenTeams);
  // Manager groups are open unless closed by hand (2026-10-27): the level is new and the teams are what the scrum reads.
  const [closedManagers, setClosedManagers] = useState<Set<string>>(readClosedManagers);
  const [shownIdle, setShownIdle] = useState<Set<string>>(() => new Set());

  // The drilled owner's team (and its manager group) opens so the highlighted row is visible.
  useEffect(() => {
    if (!activeOwner) return;
    const hit = groups.flatMap((g) => g.managers.flatMap((m) => m.teams.filter((t) => [...t.owners, ...t.idle].some((o) => o.owner === activeOwner)).map((t) => ({ team: `${g.division}/${t.team}`, manager: `${g.division}/${m.manager}` }))))[0];
    if (hit && !open.has(hit.team)) setOpen((prev) => new Set([...prev, hit.team]));
    if (hit && closedManagers.has(hit.manager)) setClosedManagers((prev) => { const next = new Set(prev); next.delete(hit.manager); return next; });
    // eslint-disable-next-line react-hooks/exhaustive-deps -- groups are derived from props; only the drilled owner matters here.
  }, [activeOwner]);

  const setAndStore = (next: Set<string>) => { setOpen(next); writeOpenTeams(next); };
  const setClosedAndStore = (next: Set<string>) => { setClosedManagers(next); writeClosedManagers(next); };
  const toggleTeam = (key: string) => {
    const next = new Set(open);
    if (next.has(key)) next.delete(key); else next.add(key);
    setAndStore(next);
  };
  const toggleManager = (key: string) => {
    const next = new Set(closedManagers);
    if (next.has(key)) next.delete(key); else next.add(key);
    setClosedAndStore(next);
  };
  const expandAll = () => { setAndStore(new Set(teamKeys)); setClosedAndStore(new Set()); };
  const collapseAll = () => { setAndStore(new Set()); setClosedAndStore(new Set(managerKeys)); };
  const allOpen = teamKeys.length > 0 && teamKeys.every((k) => open.has(k)) && closedManagers.size === 0;
  const allClosed = open.size === 0 && managerKeys.every((k) => closedManagers.has(k));

  const heat = (v: number | null, warm: number, hot: number) =>
    v == null ? "numeric" : v >= hot ? "numeric hot" : v >= warm ? "numeric warm" : "numeric";
  // The Stuck / Blocked columns include the items that are both. Under a lens they are the lens itself or empty.
  const stateCell = (key: "stuck" | "blocked", o: OperationsOwnerRowDto, group?: { target: OwnerDrillTarget; name: string }) =>
    lens && lens !== key && lens !== "stuckAndBlocked" ? <td className="numeric muted">·</td> : <td className={heat(o[key], 3, 6)}>{drillNum(o[key], group, { attention: key }, `${key} items (including Stuck + Blocked)`)}</td>;
  const mv = label ? " ops-unlensed" : "";
  const dash = (v: number | null) => (v == null ? <span className="muted">—</span> : v);

  /**
   * A count on a team / division row drills to its records (2026-10-26); the row's own click opens or closes
   * the team, so the number is a button that stops the click there. Zero stays plain text.
   */
  const drillNum = (value: number, group: { target: OwnerDrillTarget; name: string } | undefined, preset: OwnerDrillPreset | undefined, what: string) =>
    group && value > 0 ? (
      <button
        type="button"
        className="ops-link-num"
        title={`${group.name}: list ${what}`}
        aria-label={`${group.name}: list ${what}`}
        onClick={(e) => { e.stopPropagation(); onDrill(group.target, preset); }}
      >
        {value}
      </button>
    ) : value;

  /**
   * The twelve number cells, shared by owner, team, division and total rows; `group` makes the counts drill. On an
   * owner row the New assigned / Departures numbers list what the person moved in the period (2026-10-28): an owner
   * with nothing open still has a list behind their row.
   */
  const numbers = (o: OperationsOwnerRowDto, bold = false, group?: { target: OwnerDrillTarget; name: string }) => {
    const moved = (flow: "started" | "ended") => (!bold && !group ? { target: { dOwner: o.owner, dFlow: flow }, name: o.owner } : undefined);
    return (
    <>
      <td className="numeric">{drillNum(o.openOwned, group, undefined, "open items")}</td>
      <td className="numeric weighted" title={workloadBreakdown(o)}>{o.weightedOpen}</td>
      {stateCell("stuck", o, group)}
      {stateCell("blocked", o, group)}
      <td className={`numeric risk-atRisk${o.atRisk > 0 ? " flagged" : ""}`}>{o.atRisk > 0 ? drillNum(o.atRisk, group, { startRisk: "atRisk" }, "at-risk journeys") : <span className="muted">0</span>}</td>
      <td className={`numeric risk-breached${o.breached >= 3 ? " hot" : o.breached > 0 ? " warm" : ""}`}>{o.breached > 0 ? drillNum(o.breached, group, { startRisk: "breached" }, "breached journeys") : <span className="muted">0</span>}</td>
      <td className="numeric">{dash(o.oldestItemBd)}</td>
      <td className={heat(o.maxPendingBd, blockedAfterBd + 1, blockedAfterBd * 3)}>{dash(o.maxPendingBd)}</td>
      <td className={`numeric sep${mv}`}>{o.opening}</td>
      <td className={`numeric${mv}`}>{drillNum(o.newAssigned, moved("started"), undefined, "requests created this period")}</td>
      <td className={`numeric${mv}`}>{drillNum(o.departures, moved("ended"), undefined, "requests completed this period")}</td>
      <td className={`numeric${!bold && !o.identityHolds ? " hot" : ""}${mv}`} title={!bold && !o.identityHolds ? "Does not add up: opening + new − departures ≠ closing." : undefined}>{o.closing}</td>
    </>
    );
  };

  // An owner with nothing open has no desk view to show (2026-10-28): the row is plain and only its period movement drills.
  const ownerRow = (o: OperationsOwnerRowDto) => (
    o.openOwned === 0 && o.weightedJourneys === 0 ? (
    <tr key={o.owner} className={`owner idle${activeOwner === o.owner ? " active" : ""}`} title={`${o.owner}: nothing open; the New assigned and Departures numbers list what moved this period`}>
      <td><span className="ops-owner-cell"><span className="av">{initials(o.owner)}</span>{o.owner}</span></td>
      <td><span className="muted">{o.department ?? "—"}</span></td>
      {numbers(o)}
    </tr>
    ) : (
    <tr key={o.owner} className={`owner${activeOwner === o.owner ? " active" : ""}`} onClick={() => onDrill({ dOwner: o.owner })} title={`${o.owner}: list open items`}>
      <td><span className="ops-owner-cell"><span className="av">{initials(o.owner)}</span>{o.owner}</span></td>
      <td><span className="muted">{o.department ?? "—"}</span></td>
      {numbers(o)}
    </tr>
    )
  );

  const teamCount = teamKeys.length;
  return (
    <section className="ops-span-12" aria-label="Owner load">
      <div className="card">
        <div className="ops-owners-head">
          <span className="muted">
            {groups.length} {groups.length === 1 ? "division" : "divisions"} · {managerKeys.length} {managerKeys.length === 1 ? "manager" : "managers"} · {teamCount} {teamCount === 1 ? "team" : "teams"} · {owners.length} {owners.length === 1 ? "owner" : "owners"} · sorted by max pending
          </span>
          <span className="ops-owners-tools">
            <button type="button" className="btn btn-ghost btn-sm" onClick={expandAll} disabled={allOpen}>Expand all</button>
            <button type="button" className="btn btn-ghost btn-sm" onClick={collapseAll} disabled={allClosed}>Collapse all</button>
          </span>
        </div>
        {label && (
          <div className="ops-owners-lens">
            <LensTag lens={lens} pen={pen} />
            <span className="muted">
              open owned, Stuck / Blocked, oldest and max pending count the {label} items only
              {hidden > 0 ? `; ${hidden} owner${hidden === 1 ? "" : "s"} with none ${hidden === 1 ? "is" : "are"} hidden` : ""}; the period movement columns stay on everything
            </span>
          </div>
        )}
        <div className="tbl-wrap">
          <table className="tbl ops-owners">
            <thead>
              <tr className="groups">
                <th className="blank" colSpan={10} />
                <th colSpan={4} className={`sep${mv}`}>Period movement · {periodLabel}{label && <UnlensedTag />}</th>
              </tr>
              <tr>
                <th>Division / manager / team / owner</th>
                <th>Department</th>
                <th className="numeric">Open owned<InfoTooltip inline text="Open requests assigned to the owner, plus the intakes they hold while a journey waits at intake (nothing in FBO yet, or the work order waiting for its request). The desk view lists both in one table." /></th>
                <th className="numeric">Weighted open<InfoTooltip inline text={`Each open journey once, at its work type's weight, on the owner of the record carrying it (the request's assignee, else the intake's, else Unassigned). An intake, its pre-verification and its request are one journey. Weights: ${weightScale(weights, defaultWeight)}. Hover a value for the breakdown.`} /></th>
                <th className="numeric">Stuck</th>
                <th className="numeric">Blocked</th>
                <th className="numeric risk-atRisk">At risk<InfoTooltip inline text="Open journeys carried by this owner's records whose work-order version is unapproved and starts within the window (Operations:AtRiskBusinessDays). Onboarding journeys and extensions; each journey once." /></th>
                <th className="numeric risk-breached">Breached<InfoTooltip inline text="Open journeys carried by this owner's records whose work-order version is unapproved and starts today or already started. Heat: 1–2 warm, 3 or more hot." /></th>
                <th className="numeric">Oldest item (bd)</th>
                <th className="numeric sorted" title="Longest wait on a task or the work order among the owner's open items, in business days.">Max pending (bd) ▼</th>
                <th className="numeric sep">Opening owned</th>
                <th className="numeric">New assigned</th>
                <th className="numeric">Departures</th>
                <th className="numeric">Closing owned</th>
              </tr>
            </thead>
            <tbody>
              {owners.length === 0 && (
                <tr><td colSpan={14}><div className="empty"><h4>No owners</h4><p>Nothing is open or moved in this period.</p></div></td></tr>
              )}
              {groups.map((g) => (
                <Fragment key={g.division}>
                  <tr className={`division-group${activeDivision === g.division && !activeManager && !activeTeam ? " active" : ""}`} data-division={g.division}>
                    <td colSpan={2}>
                      <span className="division-group-label">{g.division}</span>
                      <span className="n"> · {g.managers.length} {g.managers.length === 1 ? "manager" : "managers"} · {g.teams.length} {g.teams.length === 1 ? "team" : "teams"} · {g.teams.reduce((n, t) => n + t.owners.length + t.idle.length, 0)} owners</span>
                    </td>
                    {numbers(g.subtotal, true, { target: { dDivision: g.division }, name: g.division })}
                  </tr>
                  {g.managers.map((m) => {
                    const managerKey = `${g.division}/${m.manager}`;
                    const managerOpen = !closedManagers.has(managerKey);
                    const managerPeople = m.teams.reduce((n, t) => n + t.owners.length + t.idle.length, 0);
                    return (
                  <Fragment key={managerKey}>
                  <tr className={`manager-group${activeDivision === g.division && activeManager === m.manager ? " active" : ""}`} data-manager={m.manager} aria-expanded={managerOpen} onClick={() => toggleManager(managerKey)} title={`${m.manager}: ${managerOpen ? "hide" : "show"} the teams`}>
                    <td>
                      <span className="material-symbols-rounded chev" aria-hidden="true">chevron_right</span>
                      <span className="manager-group-label">{m.manager}</span>
                      <span className="n"> · {m.teams.length} {m.teams.length === 1 ? "team" : "teams"} · {managerPeople} {managerPeople === 1 ? "owner" : "owners"}</span>
                    </td>
                    <td />
                    {numbers(m.subtotal, true, { target: { dDivision: g.division, dManager: m.manager }, name: `${m.manager}'s teams` })}
                  </tr>
                  {managerOpen && m.teams.map((t) => {
                    const key = `${g.division}/${t.team}`;
                    const isOpen = open.has(key);
                    const idleKey = `${key}/idle`;
                    const people = t.owners.length + t.idle.length;
                    return (
                      <Fragment key={key}>
                        <tr className={`team-group${activeDivision === g.division && activeManager === m.manager && activeTeam === t.team ? " active" : ""}`} data-team={t.team} aria-expanded={isOpen} onClick={() => toggleTeam(key)} title={`${t.team}: ${isOpen ? "hide" : "show"} the owners`}>
                          <td>
                            <span className="material-symbols-rounded chev" aria-hidden="true">chevron_right</span>
                            <span className="team-group-label">{t.team}</span>
                            <span className="n"> · {people} {people === 1 ? "owner" : "owners"}</span>
                          </td>
                          <td />
                          {numbers(t.subtotal, true, { target: { dDivision: g.division, dManager: m.manager, dTeam: t.team }, name: `${t.team}'s team` })}
                        </tr>
                        {isOpen && t.owners.map(ownerRow)}
                        {isOpen && t.idle.length > 0 && !shownIdle.has(idleKey) && (
                          <tr className="owner-fold">
                            <td colSpan={14}>
                              {t.idle.length} {t.idle.length === 1 ? "owner" : "owners"} with nothing open ({t.idle.map((o) => o.owner).join(", ")}) ·{" "}
                              <button type="button" className="btn btn-text btn-sm" onClick={() => setShownIdle((prev) => new Set([...prev, idleKey]))}>show</button>
                            </td>
                          </tr>
                        )}
                        {isOpen && shownIdle.has(idleKey) && t.idle.map(ownerRow)}
                      </Fragment>
                    );
                  })}
                  </Fragment>
                    );
                  })}
                  <tr className="subtotal division-subtotal">
                    <td>{g.division} subtotal</td>
                    <td />
                    {numbers(g.subtotal, true)}
                  </tr>
                </Fragment>
              ))}
              {owners.length > 0 && (
                <tr className="subtotal">
                  <td>All owners</td>
                  <td>All teams</td>
                  {numbers(total, true)}
                </tr>
              )}
            </tbody>
          </table>
        </div>
        <div className="ops-tbl-foot">
          <span>Owner = the work order&apos;s <b>Assigned to</b> user when there is one, else the request&apos;s, else the intake&apos;s; team = their manager in the provisioning directory (matched by alias, e-mail or full name; a lead's own records sit in the team they lead); manager = the team lead's manager; division (the directory's line of business) and department from the same directory. Open owned, Stuck, Blocked, Oldest and Max pending count the requests and the intakes an owner holds; Weighted open counts journeys (an intake, its pre-verification and its request once, on whoever holds the current record); the period movement counts requests. Click a manager to open or close its teams, a team to open its owners, an owner for the desk view, or a number on a manager, team or division row to list exactly what that row counts (At risk and Breached open the journey drawer). An owner sits in their own division, so a manager or a team whose people span two divisions appears under both, each row with its own people.</span>
          <span>Owners with nothing open fold behind &ldquo;show&rdquo; on their team; their row opens nothing, their New assigned and Departures numbers list what moved. Transferred in / out need owner-change history FBO does not keep.</span>
          {directory && <DirectoryNote directory={directory} />}
        </div>
      </div>
    </section>
  );
}
